import cloneDeep from "lodash/cloneDeep";
import moment from "moment/moment";
import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { filePaginatedKeys } from "../../../../config/reactQuery/queryKeyFactory";
import {
  ALL_EVENT_INSTANCES,
  FUTURE_EVENT_INSTANCES,
  SINGLE_EVENT_INSTANCE,
} from "../../../../constants";
import hasRecurrenceFieldChanged from "../../../../helpers/Calendar/hasRecurrenceFieldChanged";
import { uploadFileWithData } from "../../../../helpers/File";
import returnRecurrenceDates from "../../../../helpers/returnRecurrenceDates";
import { isInvalidRrule } from "../../../../helpers/rRule";
import { useEditCalendar } from "../../../../hooks/api/calendar";
import useCalendar from "../../../../hooks/api/calendar/useCalendar";
import { useSingleTask, useTaskList } from "../../../../hooks/api/tasks";
import taskKeys from "../../../../hooks/api/tasks/taskKeys";
import { toastError, toastMessage } from "../../Toast/Toast";

const useEditRecurrencePopupData = ({ popupData, closePopup }) => {
  const { refetchCalendarList } = useCalendar();
  const { refetchTaskList } = useTaskList();
  const { editInCalendar } = useEditCalendar();
  const { refetchTask } = useSingleTask(
    popupData?.item?.id,
    popupData?.item?.instanceStartDate
  );

  const [radioSelectOptions, setSelectOptions] = useState({
    [SINGLE_EVENT_INSTANCE]: false,
    [ALL_EVENT_INSTANCES]: false,
    [FUTURE_EVENT_INSTANCES]: false,
  });

  const [selectedEventDate, setSelectedEventDate] = useState({
    value: null,
    label: null,
  });

  const [loading, setLoading] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    if (
      radioSelectOptions[SINGLE_EVENT_INSTANCE] === true ||
      radioSelectOptions[FUTURE_EVENT_INSTANCES] === true ||
      radioSelectOptions[ALL_EVENT_INSTANCES] === true
    ) {
      setIsSaveDisabled(false);
    } else {
      setIsSaveDisabled(true);
    }
  }, [radioSelectOptions, selectedEventDate?.value]);

  useEffect(() => {
    if (
      radioSelectOptions[SINGLE_EVENT_INSTANCE] === true ||
      radioSelectOptions[FUTURE_EVENT_INSTANCES] === true
    ) {
      if (popupData?.form?.instanceStartDate) {
        setSelectedEventDate({
          value: popupData?.form?.instanceStartDate,
          label: moment(popupData?.form?.instanceStartDate).format(
            "dddd, MMM Do YYYY, h:mm a"
          ),
        });
      }
    }
  }, [popupData?.form?.instanceStartDate, radioSelectOptions]);

  const queryClient = useQueryClient();

  const onAddAttachment = async (photo, data = {}, progressCallback) => {
    const fileResource = await uploadFileWithData(
      photo,
      data,
      progressCallback,
      undefined,
      true,
      undefined,
      true
    );
    return fileResource;
  };

  const onUpload = useCallback(async (files, progressCallback) => {
    const handleProgressCallback = (loaded, total, filename) => {
      progressCallback(loaded, total, filename);
    };

    const result = await Promise.all(
      files.map(async ({ name, docType, isFavorited, original }) => {
        const data = {
          name,
          docType,
          isFavorited,
          contentType: original.type,
          size: original.size,
        };
        const resource = await onAddAttachment(
          original,
          data,
          (loaded, total) => handleProgressCallback(loaded, total, name)
        );

        return { ...data, ...resource };
      })
    );

    return result;
  }, []);

  const handleFilesUploaded = useCallback(async () => {
    const filteredFiles = popupData?.form?.files.reduce(
      (list, file) => {
        if (!file?.isEditing && file?.type) {
          list.latest.push(file);
          return list;
        }
        list.previous.push(file);
        return list;
      },
      { latest: [], previous: [] }
    );
    const res = await onUpload(filteredFiles.latest, () => {});

    // update files in overview
    queryClient.invalidateQueries(filePaginatedKeys.allFiles);

    return [...filteredFiles.previous, ...res];
  }, [popupData?.form?.files, onUpload, queryClient]);

  // Set the active option
  const setOptionActive = (option) => {
    setSelectOptions({
      [SINGLE_EVENT_INSTANCE]: false,
      [ALL_EVENT_INSTANCES]: false,
      [FUTURE_EVENT_INSTANCES]: false,
      [option]: true,
    });
  };

  // Show the date select dropdown if the user has selected either
  const showDropDownDateSelect =
    radioSelectOptions[FUTURE_EVENT_INSTANCES] === true ||
    radioSelectOptions[SINGLE_EVENT_INSTANCE] === true;

  const recurrenceDates = returnRecurrenceDates({
    recurrence: popupData?.item?.recurrence,
  });

  const formattedDatesArr = recurrenceDates.map((date) => {
    const formattedDate = moment(date)
      .tz(moment.tz.guess())
      .format("dddd, MMM Do YYYY, h:mm a");
    return { label: formattedDate, value: date };
  });

  const formWithUpdatesToCheck = {
    ...popupData.form,
    tags: popupData.form.currentTags
      ? popupData.form.currentTags
      : popupData.form.tags,
  };

  // Remove the unnecessary properties from the form updates object
  delete formWithUpdatesToCheck?.currentTags;
  delete formWithUpdatesToCheck?.originalResource;
  delete formWithUpdatesToCheck?.previousTask;

  const editSingleItem = async () => {
    try {
      // Handle file uploads and obtain their response data
      const fileUploadResponse = await handleFilesUploaded();

      const updatedObjectWithFiles = {
        ...formWithUpdatesToCheck,
        files: fileUploadResponse.map((file) => ({
          ref: file.reference || file.ref,
          category: file.category,
        })),
        tags: formWithUpdatesToCheck.tags.map((tag) => tag?.value || tag),
      };

      const originalResource = cloneDeep(popupData?.item);
      delete originalResource?.original;

      // Send a patch request to update the event instance
      const editArgs = {
        originalItem: originalResource,
        editedItem: updatedObjectWithFiles,
      };

      await editInCalendar({
        args: editArgs,
        operation: "$single",
      });

      /**
       * TODO: This is a temporary solution to update the task list, calendar list and task
       * we have create a way to update the lists and task with the response form "editInCalendar"
       * current solution is not performant and will hurt users with large accounts
       */

      await refetchCalendarList();
      await refetchTaskList();
      await refetchTask();

      // update tasks history
      queryClient.invalidateQueries(taskKeys.history(originalResource.id));

      const resource = originalResource?.resource;

      // Notify user of the successful update
      toastMessage(`${resource} updated successfully`);
      closePopup();
    } catch (error) {
      console.error(error);
      // Notify user of any errors during update
      toastError("Error updating event");
    }
  };

  const editAllItems = async () => {
    try {
      // Handle file uploads and obtain their response data
      const fileUploadResponse = await handleFilesUploaded();

      // Construct the request URL for updating all event instances
      // const updateRequestString = `${formWithUpdatesToCheck.id}/$${ALL_EVENT_INSTANCES}`;

      const originalResource = cloneDeep(popupData?.item);
      delete originalResource?.original;

      formWithUpdatesToCheck.recurrence =
        formWithUpdatesToCheck?.recurrence?.value ||
        formWithUpdatesToCheck?.recurrence;

      if (isInvalidRrule(formWithUpdatesToCheck?.recurrence)) {
        formWithUpdatesToCheck.recurrence = undefined;
      }

      const hasRecurrenceChanged = hasRecurrenceFieldChanged(
        originalResource,
        formWithUpdatesToCheck
      );

      if (hasRecurrenceChanged && formWithUpdatesToCheck.recurrence) {
        // get the recurrence string from the recurrence object
        const recString = formWithUpdatesToCheck.recurrence;
        const rruleLessDTstart =
          recString.split("\n")?.length > 1
            ? recString.split("\n")[1]
            : recString;

        // get the start date from the form state
        const dtStart = `DTSTART:${moment(formWithUpdatesToCheck.startDate)
          .utc()
          .format("YYYYMMDDTHHmmss")}Z`;
        // combine the start date and the recurrence string
        const fullRrule = `${dtStart}\n${rruleLessDTstart}`;
        // set the recurrence string to the full rrule
        formWithUpdatesToCheck.recurrence = fullRrule;
      }

      // Merge the uploaded files into the form data
      const updatedObjectWithFiles = {
        ...formWithUpdatesToCheck,
        tags: formWithUpdatesToCheck.tags.map((tag) => tag?.value || tag),
        files: fileUploadResponse.map((file) => ({
          ref: file.reference || file.ref,
          category: file.category,
        })),
      };

      // Send a patch request to update the event instances

      const editArgs = {
        originalItem: originalResource,
        editedItem: updatedObjectWithFiles,
      };

      await editInCalendar({
        args: editArgs,
        operation: "$all",
      });

      /**
       * TODO: This is a temporary solution to update the task list, calendar list and task
       * we have create a way to update the lists and task with the response form "editInCalendar"
       * current solution is not performant and will hurt users with large accounts
       */
      await refetchCalendarList();
      await refetchTaskList();
      await refetchTask();

      closePopup();
      queryClient.invalidateQueries(taskKeys.history(originalResource.id));

      const resource = originalResource?.resource;
      toastMessage(`${resource}s updated successfully`);
    } catch (error) {
      console.error(error);
      // Notify user of any errors during update
      toastError("Error updating event");
    }
  };

  const editFutureItems = async () => {
    let updateResponse = {};
    try {
      // Handle file uploads if any and receive response
      const fileUploadResponse = await handleFilesUploaded();

      formWithUpdatesToCheck.recurrence =
        formWithUpdatesToCheck?.recurrence?.value ||
        formWithUpdatesToCheck?.recurrence;

      if (isInvalidRrule(formWithUpdatesToCheck?.recurrence)) {
        formWithUpdatesToCheck.recurrence = undefined;
      }

      const newSplitRecurrence = {
        recurrence: formWithUpdatesToCheck?.recurrence,
      };

      const originalResource = cloneDeep(popupData?.item);
      delete originalResource?.original;

      // Construct the updated object, including file references
      const updatedObjectWithFiles = {
        ...formWithUpdatesToCheck,
        tags: formWithUpdatesToCheck.tags.map((tag) => tag?.value || tag),
        files: fileUploadResponse.map((file) => ({
          ref: file.reference || file.ref,
          category: file.category,
        })),
        ...newSplitRecurrence,
      };

      const editArgs = {
        originalItem: originalResource,
        editedItem: updatedObjectWithFiles,
      };

      const editParams = { args: editArgs, operation: "$future" };

      const { data } = await editInCalendar(editParams);
      updateResponse = data;
      /**
       * TODO: This is a temporary solution to update the task list, calendar list and task
       * we have create a way to update the lists and task with the response form "editInCalendar"
       * current solution is not performant and will hurt users with large accounts
       */

      await refetchCalendarList();
      await refetchTaskList();

      // update tasks history
      queryClient.invalidateQueries(taskKeys.history(originalResource.id));

      const resource = originalResource?.resource;

      toastMessage(`${resource}s updated successfully`);
      closePopup();
    } catch (error) {
      console.error(error);
      // In case of any errors, show an error message
      toastError("Error updating event");
    }
    // updateResponse is returned into singleTaskView cleanExit fn to setCurrentTask
    return updateResponse;
  };

  const handleSubmit = async () => {
    setLoading(true);
    // Initialize the response variable to hold the updated items response
    let updateResponse = null;

    // If the single event instance option is selected, call the editSingleItem function
    if (radioSelectOptions[SINGLE_EVENT_INSTANCE] === true) {
      updateResponse = await editSingleItem();
    }

    // If the all event instances option is selected, call the editAllItems function
    if (radioSelectOptions[ALL_EVENT_INSTANCES] === true) {
      updateResponse = await editAllItems();
    }

    // If the future event instances option is selected, call the editFutureItems function
    if (radioSelectOptions[FUTURE_EVENT_INSTANCES] === true) {
      updateResponse = await editFutureItems({ reloadAll: true });
    }

    // if the popupData has a handleCleanExit function, call it.
    if (popupData.handleCleanExit) {
      // get the radio selection option that is true
      const selectedOption = Object.keys(radioSelectOptions).find(
        (option) => radioSelectOptions[option]
      );
      // call the handleCleanExit function with the response and selected option
      popupData.handleCleanExit({
        updateResponse,
        selectedOption,
        selectedEventDate,
      });
    }
    // Set the loading state to false after the request is complete
    setLoading(false);
  };

  const handleExitClick = () => {
    if (popupData?.onCancelCleanExitFn) popupData?.onCancelCleanExitFn();
    closePopup();
  };

  return {
    radioSelectOptions,
    showDropDownDateSelect,
    formattedDatesArr,
    selectedEventDate,
    loading,
    handleSubmit,
    setOptionActive,
    setSelectedEventDate,
    handleExitClick,
    isSaveDisabled,
    setIsDropdownOpen,
    isDropdownOpen,
  };
};

export default useEditRecurrencePopupData;
